/**
 * Secure token storage utilities
 * 
 * While localStorage is vulnerable to XSS, these utilities provide:
 * 1. Basic encoding to prevent casual inspection
 * 2. Consistent token management interface
 * 3. Token expiration checking
 * 4. Easy migration path if switching storage methods
 */

const TOKEN_KEY = 'auth_token';
const TOKEN_EXPIRY_KEY = 'auth_token_expiry';

/**
 * Simple base64 encoding to hide token from casual inspection
 * Note: This is NOT encryption - it only makes tokens less visible
 * For true security, rely on XSS prevention (CSP, input sanitization)
 */
const encode = (value: string): string => {
  try {
    return btoa(encodeURIComponent(value));
  } catch (error) {
    console.error('[TokenStorage] Encoding failed:', error);
    // Fail securely - don't store plain text token
    throw new Error('Token encoding failed - cannot store token securely');
  }
};

const decode = (value: string): string => {
  try {
    return decodeURIComponent(atob(value));
  } catch (error) {
    console.error('[TokenStorage] Decoding failed:', error);
    // If decoding fails, the stored value might be plain text (from old version)
    // Try to return as-is, but log warning
    console.warn('[TokenStorage] Token decoding failed - stored value may be corrupted or plain text');
    throw new Error('Token decoding failed - stored token may be invalid');
  }
};

/**
 * Store token with expiration tracking
 */
export const setToken = (token: string): void => {
  try {
    // Decode token to check expiration
    const payload = JSON.parse(atob(token.split('.')[1]));
    const expiry = payload.exp ? payload.exp * 1000 : null; // Convert to milliseconds
    
    // Store encoded token
    const encodedToken = encode(token);
    localStorage.setItem(TOKEN_KEY, encodedToken);
    
    // Store expiry for quick checks
    if (expiry) {
      localStorage.setItem(TOKEN_EXPIRY_KEY, expiry.toString());
    }
  } catch (error) {
    console.error('[TokenStorage] Error storing token:', error);
    // Fail securely - don't store token if encoding fails
    // This prevents storing plain text tokens
    throw error;
  }
};

/**
 * Retrieve token (decoded from storage)
 */
export const getToken = (): string | null => {
  try {
    const encodedToken = localStorage.getItem(TOKEN_KEY);
    if (!encodedToken) {
      return null;
    }
    
    // Try to decode - if it fails, token might be corrupted
    try {
      return decode(encodedToken);
    } catch (decodeError) {
      // Decoding failed - token might be from old version (plain text) or corrupted
      // Remove corrupted token to force re-authentication
      console.warn('[TokenStorage] Token decode failed - may be corrupted or from old version');
      removeToken();
      return null;
    }
  } catch (error) {
    console.error('[TokenStorage] Unexpected error getting token:', error);
    return null;
  }
};

/**
 * Remove token and expiry
 */
export const removeToken = (): void => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(TOKEN_EXPIRY_KEY);
};

/**
 * Check if token is expired (without making API call)
 */
export const isTokenExpired = (): boolean => {
  const token = getToken();
  if (!token) {
    return true;
  }

  try {
    const expiryStr = localStorage.getItem(TOKEN_EXPIRY_KEY);
    if (expiryStr) {
      const expiry = parseInt(expiryStr, 10);
      return Date.now() >= expiry;
    }

    // Fallback: decode token to check expiry
    const payload = JSON.parse(atob(token.split('.')[1]));
    if (payload.exp) {
      const expiry = payload.exp * 1000;
      return Date.now() >= expiry;
    }
  } catch (error) {
    console.error('[TokenStorage] Error checking token expiration:', error);
    return true; // Assume expired if we can't check
  }

  return false;
};

/**
 * Get token expiration time in milliseconds
 */
export const getTokenExpiry = (): number | null => {
  try {
    const expiryStr = localStorage.getItem(TOKEN_EXPIRY_KEY);
    if (expiryStr) {
      return parseInt(expiryStr, 10);
    }

    const token = getToken();
    if (token) {
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (payload.exp) {
        return payload.exp * 1000;
      }
    }
  } catch (error) {
    console.error('[TokenStorage] Error getting token expiry:', error);
  }

  return null;
};

/**
 * Get time until token expires in milliseconds
 * Returns negative if already expired
 */
export const getTimeUntilExpiry = (): number | null => {
  const expiry = getTokenExpiry();
  if (!expiry) {
    return null;
  }
  return expiry - Date.now();
};


